import { useState } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import {
  FiHome,
  FiBook,
  FiShoppingCart,
  FiUsers,
  FiImage,
  FiTag,
  FiMail,
  FiGrid,
  FiLogOut,
  FiMenu,
  FiX,
  FiRotateCcw,
  FiEdit3,
  FiStar,
  FiFileText,
  FiTruck,
  FiGlobe,
  FiCreditCard,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { storeUrl } from '../config/urls';

const navItems = [
  { path: '/', label: 'Dashboard', icon: FiHome },
  { path: '/books', label: 'Books', icon: FiBook },
  { path: '/orders', label: 'Orders', icon: FiShoppingCart },
  { path: '/returns', label: 'Returns', icon: FiRotateCcw },
  { path: '/return-reasons', label: 'Return Reasons', icon: FiEdit3 },
  { path: '/users', label: 'Users', icon: FiUsers },
  { path: '/categories', label: 'Categories', icon: FiGrid },
  { path: '/banners', label: 'Banners', icon: FiImage },
  { path: '/coupons', label: 'Coupons', icon: FiTag },
  { path: '/blog', label: 'Blog', icon: FiEdit3 },
  { path: '/reviews', label: 'Reviews', icon: FiStar },
  { path: '/site-content', label: 'Site Content', icon: FiFileText },
  { path: '/home-sections', label: 'Home Sections', icon: FiHome },
  { path: '/shipping', label: 'Shipping', icon: FiTruck },
  { path: '/countries', label: 'Countries', icon: FiGlobe },
  { path: '/messages', label: 'Messages', icon: FiMail },
  { path: '/payment-settings', label: 'Payment Settings', icon: FiCreditCard },
];

const AdminLayout = () => {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-black text-lg">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 max-w-[80vw] bg-gray-900 text-white transform transition-transform lg:translate-x-0 lg:static lg:z-auto flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
          <Link to="/" className="text-base sm:text-lg font-bold text-orange-400 truncate">
            Ekamithila Admin
          </Link>
          <button
            className="lg:hidden text-white hover:text-orange-300"
            onClick={() => setSidebarOpen(false)}
          >
            <FiX size={20} />
          </button>
        </div>

        <nav className="mt-4 px-3 space-y-1 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? 'bg-orange-600 text-white'
                    : 'text-white hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="text-sm text-white font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-white hover:text-orange-300 w-full"
          >
            <FiLogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3 flex items-center gap-3 sm:gap-4 lg:px-6">
          <button className="lg:hidden text-black" onClick={() => setSidebarOpen(true)}>
            <FiMenu size={22} />
          </button>
          <h1 className="text-base sm:text-lg font-semibold text-black truncate">
            {navItems.find((i) => i.path === location.pathname)?.label || 'Admin Panel'}
          </h1>
          <a
            href={storeUrl('/')}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs sm:text-sm text-orange-600 hover:underline whitespace-nowrap"
          >
            <span className="hidden sm:inline">View Store</span>
            <span className="sm:hidden">Store</span> &rarr;
          </a>
        </header>

        <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
